import { createAsyncThunk } from "@reduxjs/toolkit";

import api from "api";
import { notificationDisplay } from "actions/notification";
import { proceedThunk } from "./utils";

export const authenticateThunk = createAsyncThunk(
  "auth/authenticate",
  async (_, { rejectWithValue }) => {
    const response = await api.auth.authenticate();
    const data = await response.json();
    // (status in the range 200 – 299
    if (!response.ok) {
      const error = data.detail;
      return rejectWithValue(error);
    }
    // TODO add some received data validation
    return data;
  }
);

export const loginThunk = createAsyncThunk(
  "auth/login",
  async (payload, { rejectWithValue }) => {
    const response = await api.auth.login(payload);
    const data = await response.json();
    if (!response.ok) {
      return rejectWithValue(data);
    }
    return data;
  }
);

export const oauth2LoginThunk = createAsyncThunk(
  "auth/oauth2Login",
  async (payload, { rejectWithValue }) => {
    const response = await api.auth.oauth2Login(payload);
    const data = await response.json();
    if (!response.ok) {
      return rejectWithValue(data);
    }
    return data;
  }
);

export const oauth2GetAuthUriThunk = createAsyncThunk(
  "auth/oauth2GetAuthUriThunk",
  async (payload, { rejectWithValue }) => {
    const response = await api.auth.oauth2GetAuthUri(payload);
    const data = await response.json();
    if (!response.ok) {
      const error = data.detail;
      return rejectWithValue(error);
    }
    return data;
  }
);

export const logoutThunk = createAsyncThunk(
  "auth/logout",
  async (_, { rejectWithValue }) => {
    const response = await api.auth.logout();
    const data = await response.json();
    if (!response.ok) {
      const error = data.detail;
      return rejectWithValue(error);
    }
    return data;
  }
);

export const signupThunk = createAsyncThunk(
  "auth/signup",
  async (payload, { rejectWithValue }) => {
    const response = await api.auth.signup(payload);
    const data = await response.json();
    if (!response.ok) {
      return rejectWithValue(data);
    }
    return data;
  }
);

export const verifyEmailThunk = createAsyncThunk(
  "auth/verifyEmail",
  async (payload, { rejectWithValue }) => {
    const response = await api.auth.verifyEmail(payload);
    const data = await response.json();
    if (!response.ok) {
      const error = data.detail;
      return rejectWithValue(error);
    }
    return data;
  }
);

export const resendVerifyEmailThunk = createAsyncThunk(
  "auth/resendVerifyEmail",
  async (_payload, { rejectWithValue }) => {
    const response = await api.auth.resendVerifyEmail();
    const data = await response.json();
    if (!response.ok) {
      const error = data.detail;
      return rejectWithValue(error);
    }
    return data;
  }
);

export const forgotPasswordThunk = createAsyncThunk(
  "auth/forgotPassword",
  async (payload, { rejectWithValue }) => {
    const response = await api.auth.forgotPassword(payload);
    const data = await response.json();
    if (!response.ok) {
      const error = data.detail;
      return rejectWithValue(error);
    }
    return data;
  }
);

export const changePasswordThunk = createAsyncThunk(
  "auth/changePassword",
  async (payload, { rejectWithValue }) => {
    const response = await api.auth.changePassword(payload);
    const data = await response.json();
    if (!response.ok) {
      const error = data.detail;
      return rejectWithValue(error);
    }
    return data;
  }
);

export const usersListThunk = createAsyncThunk(
  "auth/usersList",
  async (payload, { rejectWithValue }) => {
    const response = await api.auth.getUsers(payload);
    const data = await response.json();
    if (!response.ok) {
      const error = data.detail;
      return rejectWithValue(error);
    }
    return data;
  }
);

export const groupsListThunk = createAsyncThunk(
  "auth/groupsList",
  async (payload, { rejectWithValue }) => {
    const response = await api.auth.getGroups(payload);
    const data = await response.json();
    if (!response.ok) {
      const error = data.detail;
      return rejectWithValue(error);
    }
    return data;
  }
);

export const searchUsersAndGroupsThunk = createAsyncThunk(
  "auth/searchUsersAndGroups",
  async (payload, { rejectWithValue }) => {
    const [responseUsers, responseGroups] = await Promise.all([
      api.auth.getUsers(payload),
      api.auth.getGroups(payload),
    ]);
    const [dataUsers, dataGroups] = await Promise.all([
      responseUsers.json(),
      responseGroups.json(),
    ]);
    if (!responseUsers.ok || !responseGroups.ok) {
      const error = "Failed to search users/groups";
      return rejectWithValue(error);
    }
    return { users: dataUsers.list, groups: dataGroups.list, query: undefined };
  }
);

export const ownersListThunk = createAsyncThunk(
  "auth/ownersList",
  async (payload, { rejectWithValue }) => {
    const response = await api.auth.getOwners(payload);
    const data = await response.json();
    if (!response.ok) {
      const error = data.detail;
      return rejectWithValue(error);
    }
    return data;
  }
);

export const getCreditsThunk = createAsyncThunk(
  "auth/getCredits",
  async (_, { rejectWithValue }) => {
    const response = await api.auth.getCredits();
    const data = await response.json();
    if (!response.ok) {
      const error = data.detail;
      return rejectWithValue(error);
    }
    return data;
  }
);

export const sendPromoCodeThunk = createAsyncThunk(
  "auth/sendPromoCode",
  proceedThunk(api.auth.promoCode)
);

export const updateTLevelThunk = createAsyncThunk(
  "auth/updateTlevel",
  async (payload, { rejectWithValue }) => {
    const response = await api.auth.trust(payload);
    const data = await response.json();
    if (!response.ok) {
      const error = data.detail;
      return rejectWithValue(error);
    }
    return data;
  }
);

export const updateUserDomainInfoThunk = createAsyncThunk(
  "auth/updateUserDomainInfo",
  async (payload, { rejectWithValue }) => {
    const response = await api.auth.updateUserDomainInfo(payload);
    const data = await response.json();
    if (!response.ok) {
      const error = data.detail;
      return rejectWithValue(error);
    }
    return data;
  }
);

export const fetchUserThunk = createAsyncThunk(
  "auth/fetchUser",
  async (payload, { rejectWithValue }) => {
    const response = await api.auth.getUser(payload);
    const data = await response.json();
    if (!response.ok) {
      const error = data.detail;
      return rejectWithValue(error);
    }
    return data;
  }
);

export const sendFeedbackThunk = createAsyncThunk(
  "auth/sendFeedback",
  async (payload, { rejectWithValue }) => {
    const response = await api.auth.sendFeedback(payload);
    const data = await response.json();
    if (!response.ok) {
      const error = data.detail;
      return rejectWithValue(error);
    }
    return data;
  }
);

export const sendGoogleEventThunk = createAsyncThunk(
  "auth/sendGoogleEvent",
  async (payload, { rejectWithValue }) => {
    const response = await api.auth.sendGoogleEvent(payload);
    const data = await response.json();
    if (!response.ok) {
      const error = data.detail;
      return rejectWithValue(error);
    }
    return data;
  }
);

export const updateLocalStorageThunk = createAsyncThunk(
  "auth/localStorage/update",
  proceedThunk(api.auth.updateUserLocalStorage)
);

export const updateDemoWizardThunk = createAsyncThunk(
  "auth/demoWizard/update",
  proceedThunk(api.auth.updateDemoWizard)
);

export const deleteAccountThunk = createAsyncThunk(
  "auth/delete",
  proceedThunk(api.auth.deleteAccount)
);

export const updateSettingsThunk = createAsyncThunk(
  "auth/settings/update",
  proceedThunk(api.auth.updateSettings)
);

export const getApiKeyThunk = createAsyncThunk(
  "apiKey/get",
  proceedThunk(api.auth.apiKey.get)
);

export const createApiKeyThunk = createAsyncThunk(
  "apiKey/create",
  proceedThunk(api.auth.apiKey.create)
);

export const deleteApiKeyThunk = createAsyncThunk(
  "apiKey/delete",
  proceedThunk(api.auth.apiKey.delete)
);

export const npsSurveyThunk = createAsyncThunk(
  "survey/nps",
  proceedThunk(api.auth.npsSurvey)
);
